import { useEffect, useMemo, useState } from 'react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Sprint B / ADR-019 — Onglet « Invitations externes » dans le
 * DossierAccessPanel. Permet à l'avocat M24 de :
 *  - inviter un externe (email + nom + type + permissions par onglet)
 *  - lister les invitations actives + révoquées
 *  - modifier les permissions à la volée
 *  - révoquer (avec raison obligatoire)
 *
 * Le clear_token_one_time est affiché UNE FOIS après création
 * (en attendant Brevo email en prod) — l'avocat le copie + l'envoie
 * à l'invité par un canal sécurisé.
 */

type Permissions = {
  can_view_analyse: boolean;
  can_view_pieces: boolean;
  can_view_inventaire: boolean;
  can_view_doc_travail: boolean;
  can_view_dossier_travail: boolean;
  can_view_pieces_client: boolean;
  can_view_timeline: boolean;
  can_view_recherche: boolean;
  can_view_chat_ia: boolean;
  can_view_note_generale: boolean;
  can_view_journal: boolean;
  can_comment: boolean;
  can_upload_pieces_client: boolean;
  can_edit_inventaire: boolean;
  can_edit_doc_travail: boolean;
  can_edit_dossier_travail: boolean;
  can_edit_timeline: boolean;
  can_edit_note_generale: boolean;
};

/** Features avec leur paire view/edit (ADR-030 — granularité lecture/écriture).
 *  ``editKey`` null = feature read-only par nature (pas d'action d'édition). */
type FeatureRow = {
  label: string;
  viewKey: keyof Permissions;
  editKey: keyof Permissions | null;
  editLabel?: string; // libellé spécifique pour le bouton "écriture"
};

const FEATURES: FeatureRow[] = [
  { label: 'Analyse', viewKey: 'can_view_analyse', editKey: null },
  { label: 'Pièces (PDF greffe)', viewKey: 'can_view_pieces', editKey: null },
  {
    label: 'Inventaire',
    viewKey: 'can_view_inventaire',
    editKey: 'can_edit_inventaire',
  },
  {
    label: 'Doc travail (annotations + notes)',
    viewKey: 'can_view_doc_travail',
    editKey: 'can_edit_doc_travail',
  },
  {
    label: 'Dossier de travail (marqueurs)',
    viewKey: 'can_view_dossier_travail',
    editKey: 'can_edit_dossier_travail',
  },
  {
    label: 'Pièces client',
    viewKey: 'can_view_pieces_client',
    editKey: 'can_upload_pieces_client',
    editLabel: 'Uploader',
  },
  {
    label: 'Timeline',
    viewKey: 'can_view_timeline',
    editKey: 'can_edit_timeline',
  },
  { label: 'Recherche', viewKey: 'can_view_recherche', editKey: null },
  { label: 'Recherche IA (chat Claude)', viewKey: 'can_view_chat_ia', editKey: null },
  {
    label: 'Note générale',
    viewKey: 'can_view_note_generale',
    editKey: 'can_edit_note_generale',
  },
  { label: "Journal d'audit", viewKey: 'can_view_journal', editKey: null },
];

type Invitation = {
  id: string;
  dossier_id: string;
  invited_email: string;
  invited_display_name: string;
  invitation_type: 'client' | 'co_conseil' | 'expert' | 'autre';
  reason_for_access: string;
  status: 'pending' | 'account_created' | 'accepted' | 'revoked' | 'expired';
  permissions: Record<string, boolean>;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  charter_version: string;
  charter_accepted_at: string | null;
  created_at: string;
  clear_token_one_time?: string;
  email_sent?: boolean;
};

// PERM_LABELS supprimé — remplacé par la liste FEATURES + le rendu en table view/edit.

const DEFAULT_PERMS_BY_TYPE: Record<Invitation['invitation_type'], Partial<Permissions>> = {
  client: { can_view_pieces_client: true, can_comment: true, can_upload_pieces_client: true },
  co_conseil: {
    can_view_pieces: true,
    can_view_doc_travail: true,
    can_view_pieces_client: true,
    can_view_timeline: true,
    can_view_inventaire: true,
    can_comment: true,
    can_upload_pieces_client: true,
  },
  expert: {
    can_view_pieces: true,
    can_view_pieces_client: true,
    can_view_inventaire: true,
    can_comment: true,
  },
  autre: { can_view_pieces_client: true, can_comment: true },
};

function emptyPerms(): Permissions {
  return {
    can_view_analyse: false,
    can_view_pieces: false,
    can_view_inventaire: false,
    can_view_doc_travail: false,
    can_view_dossier_travail: false,
    can_view_pieces_client: false,
    can_view_timeline: false,
    can_view_recherche: false,
    can_view_chat_ia: false,
    can_view_note_generale: false,
    can_view_journal: false,
    can_comment: false,
    can_upload_pieces_client: false,
    can_edit_inventaire: false,
    can_edit_doc_travail: false,
    can_edit_dossier_travail: false,
    can_edit_timeline: false,
    can_edit_note_generale: false,
  };
}

export default function InvitationsPanel({ dossierId }: { dossierId: string }) {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [oneTimeToken, setOneTimeToken] = useState<{
    invitationId: string;
    token: string;
    email: string;
    emailSent: boolean;
  } | null>(null);

  const [draft, setDraft] = useState<{
    email: string;
    display_name: string;
    invitation_type: Invitation['invitation_type'];
    reason_for_access: string;
    expires_in_days: number;
    permissions: Permissions;
  }>(() => ({
    email: '',
    display_name: '',
    invitation_type: 'client',
    reason_for_access: '',
    expires_in_days: 30,
    permissions: { ...emptyPerms(), ...DEFAULT_PERMS_BY_TYPE.client },
  }));
  // Édition de la raison après coup, par id d'invitation.
  const [reasonEditing, setReasonEditing] = useState<{ id: string; draft: string } | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const rows = await api.get<Invitation[]>(`/dossiers/${dossierId}/invitations`);
      setInvitations(rows);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const onTypeChange = (t: Invitation['invitation_type']) => {
    setDraft((prev) => ({
      ...prev,
      invitation_type: t,
      permissions: { ...emptyPerms(), ...DEFAULT_PERMS_BY_TYPE[t] },
    }));
  };

  const create = async () => {
    if (!draft.email.trim() || !draft.display_name.trim()) {
      toast.error('Email et nom requis');
      return;
    }
    if (!draft.reason_for_access.trim()) {
      toast.error("Indique la raison de l'accès (texte libre, conservé pour traçabilité)");
      return;
    }
    setCreating(true);
    try {
      const resp = await api.post<Invitation>(`/dossiers/${dossierId}/invitations`, {
        email: draft.email.trim(),
        display_name: draft.display_name.trim(),
        invitation_type: draft.invitation_type,
        reason_for_access: draft.reason_for_access.trim(),
        expires_in_days: draft.expires_in_days,
        permissions: draft.permissions,
      });
      if (resp.clear_token_one_time) {
        setOneTimeToken({
          invitationId: resp.id,
          token: resp.clear_token_one_time,
          email: resp.invited_email,
          emailSent: resp.email_sent === true,
        });
      }
      setShowForm(false);
      setDraft({
        email: '',
        display_name: '',
        invitation_type: 'client',
        reason_for_access: '',
        expires_in_days: 30,
        permissions: { ...emptyPerms(), ...DEFAULT_PERMS_BY_TYPE.client },
      });
      await reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setCreating(false);
    }
  };

  const saveReason = async (inv: Invitation, newReason: string) => {
    if (!newReason.trim()) {
      toast.error("La raison ne peut pas être vide");
      return;
    }
    try {
      await api.patch(`/invitations/${inv.id}`, { reason_for_access: newReason.trim() });
      setReasonEditing(null);
      toast.success("Raison mise à jour");
      await reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const revoke = async (inv: Invitation) => {
    const reason = window.prompt(
      `Raison de la révocation pour ${inv.invited_email} ? (obligatoire)`
    );
    if (!reason || !reason.trim()) return;
    if (
      !window.confirm(
        `Révoquer définitivement l'accès de ${inv.invited_email} à ce dossier ?\n\n` +
          'Toutes ses sessions actives seront déconnectées dans les 15 minutes max.'
      )
    )
      return;
    try {
      await api.delete(`/invitations/${inv.id}`);
      toast.success('Accès révoqué');
      await reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const updatePermissions = async (inv: Invitation, perms: Record<string, boolean>) => {
    try {
      await api.patch(`/invitations/${inv.id}`, { permissions: perms });
      await reload();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const active = useMemo(() => invitations.filter((i) => i.status !== 'revoked'), [invitations]);
  const revoked = useMemo(() => invitations.filter((i) => i.status === 'revoked'), [invitations]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-[15px] font-medium text-text-primary">
          Invitations externes
        </h3>
        {!showForm && (
          <Btn variant="primary" size="sm" onClick={() => setShowForm(true)}>
            + Inviter
          </Btn>
        )}
      </div>

      {oneTimeToken && (
        <div
          className={`rounded-md border p-3 text-[12px] ${
            oneTimeToken.emailSent
              ? 'border-cat-fav/40 bg-cat-fav-pastel'
              : 'border-cat-imp/40 bg-cat-imp-pastel'
          }`}
        >
          {oneTimeToken.emailSent ? (
            <p className="mb-1 font-medium text-cat-fav-text">
              ✅ Email d'invitation envoyé à {oneTimeToken.email}
            </p>
          ) : (
            <p className="mb-1 font-medium text-cat-imp-text">
              ⚠ Email Brevo non envoyé — transmets ce lien manuellement à {oneTimeToken.email} (UNE
              SEULE FOIS)
            </p>
          )}
          <code className="block break-all rounded bg-paper-elevated p-2 font-mono text-[11px]">
            https://adr.m24-avocats.be/external/accept?token={oneTimeToken.token}
          </code>
          <p className="mt-1 text-text-tertiary">
            {oneTimeToken.emailSent
              ? "Lien conservé ici en backup. Tu peux le copier si l'email n'arrive pas."
              : "Vérifie BREVO_API_KEY dans la config prod. En attendant, copie le lien et envoie-le par un canal sécurisé."}
          </p>
          <div className="mt-2 flex gap-2">
            <Btn
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(
                  `https://adr.m24-avocats.be/external/accept?token=${oneTimeToken.token}`
                );
                toast.success('Lien copié');
              }}
            >
              📋 Copier
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setOneTimeToken(null)}>
              Fermer
            </Btn>
          </div>
        </div>
      )}

      {showForm && (
        <div className="space-y-3 rounded-md border border-rule bg-paper-elevated p-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-[12px]">
              <span className="block text-text-secondary">Email invité</span>
              <input
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
              />
            </label>
            <label className="text-[12px]">
              <span className="block text-text-secondary">Nom usuel</span>
              <input
                type="text"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
              />
            </label>
            <label className="text-[12px]">
              <span className="block text-text-secondary">Type d'invité</span>
              <select
                value={draft.invitation_type}
                onChange={(e) => onTypeChange(e.target.value as Invitation['invitation_type'])}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
              >
                <option value="client">Client</option>
                <option value="co_conseil">Co-conseil</option>
                <option value="expert">Expert</option>
                <option value="autre">Autre</option>
              </select>
            </label>
            <label className="text-[12px]">
              <span className="block text-text-secondary">Durée d'invitation (jours)</span>
              <input
                type="number"
                min={1}
                max={90}
                value={draft.expires_in_days}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    expires_in_days: Math.max(1, parseInt(e.target.value, 10) || 30),
                  })
                }
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
              />
            </label>
          </div>

          <label className="block text-[12px]">
            <span className="block text-text-secondary">
              Raison de l'accès <span className="text-cat-def">*</span>
            </span>
            <textarea
              value={draft.reason_for_access}
              onChange={(e) => setDraft({ ...draft, reason_for_access: e.target.value })}
              rows={2}
              placeholder="Ex. : consultation des pièces avant la réunion du 10/06 — client invité par Me TÖLLER"
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-card px-3 py-2 text-[13px]"
              maxLength={2000}
              required
            />
            <span className="mt-0.5 block text-[11px] text-text-tertiary">
              Conservé indéfiniment pour traçabilité. Apparaît dans l'email d'invitation et reste
              visible dans la liste des invitations du dossier.
            </span>
          </label>

          <details className="rounded-md border border-rule bg-paper-card p-2 text-[12px]" open>
            <summary className="cursor-pointer font-medium text-text-primary">
              Permissions ({Object.values(draft.permissions).filter(Boolean).length} activées)
            </summary>
            <table className="mt-2 w-full text-[12px]">
              <thead>
                <tr className="text-text-tertiary text-[11px]">
                  <th className="text-left font-normal pb-1">Fonctionnalité</th>
                  <th className="text-center font-normal pb-1 w-16">Lecture</th>
                  <th className="text-center font-normal pb-1 w-20">Écriture</th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f) => {
                  const viewChecked = draft.permissions[f.viewKey];
                  const editChecked = f.editKey ? draft.permissions[f.editKey] : false;
                  return (
                    <tr key={f.viewKey} className="border-t border-rule">
                      <td className="py-1 pr-2">{f.label}</td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={viewChecked}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              permissions: {
                                ...draft.permissions,
                                [f.viewKey]: e.target.checked,
                                // Désactiver l'édition si on retire la lecture
                                ...(!e.target.checked && f.editKey ? { [f.editKey]: false } : {}),
                              },
                            })
                          }
                        />
                      </td>
                      <td className="text-center">
                        {f.editKey ? (
                          <input
                            type="checkbox"
                            checked={editChecked}
                            disabled={!viewChecked}
                            title={
                              !viewChecked
                                ? "Activez d'abord la lecture"
                                : (f.editLabel ?? 'Écriture')
                            }
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                permissions: {
                                  ...draft.permissions,
                                  [f.editKey!]: e.target.checked,
                                },
                              })
                            }
                          />
                        ) : (
                          <span className="text-text-tertiary text-[10px]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t border-rule">
                  <td className="py-1 pr-2">Ajouter des commentaires</td>
                  <td className="text-center" colSpan={2}>
                    <input
                      type="checkbox"
                      checked={draft.permissions.can_comment}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          permissions: {
                            ...draft.permissions,
                            can_comment: e.target.checked,
                          },
                        })
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </details>

          <div className="flex gap-2">
            <Btn variant="primary" size="sm" disabled={creating} onClick={() => void create()}>
              {creating ? 'Envoi…' : "Créer l'invitation"}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setShowForm(false)} disabled={creating}>
              Annuler
            </Btn>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-text-tertiary">Chargement…</p>
      ) : (
        <>
          <ul className="space-y-2">
            {active.length === 0 && (
              <li className="rounded-md border border-dashed border-rule-strong bg-paper-card p-4 text-center text-[12px] text-text-tertiary">
                Aucune invitation active.
              </li>
            )}
            {active.map((inv) => (
              <li
                key={inv.id}
                className="rounded-md border border-rule bg-paper-elevated p-3 text-[12px]"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <div className="font-medium text-text-primary">
                      {inv.invited_display_name}{' '}
                      <span className="font-normal text-text-tertiary">({inv.invited_email})</span>
                    </div>
                    <div className="text-[11px] text-text-tertiary">
                      {inv.invitation_type} · {inv.status} · expire{' '}
                      {new Date(inv.expires_at).toLocaleDateString('fr-BE')}
                    </div>
                  </div>
                  <Btn
                    variant="ghost"
                    size="sm"
                    onClick={() => void revoke(inv)}
                    className="text-cat-def hover:bg-cat-def-pastel"
                  >
                    🚨 Révoquer
                  </Btn>
                </div>

                <div className="mt-2 rounded-md border border-rule bg-paper-card p-2">
                  <div className="mb-0.5 flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-[0.5px] text-text-tertiary">
                      Raison de l'accès
                    </span>
                    {reasonEditing?.id !== inv.id && (
                      <button
                        type="button"
                        onClick={() =>
                          setReasonEditing({ id: inv.id, draft: inv.reason_for_access ?? '' })
                        }
                        className="text-[10px] text-accent-text hover:underline"
                      >
                        ✏ Modifier
                      </button>
                    )}
                  </div>
                  {reasonEditing?.id === inv.id ? (
                    <div className="space-y-1.5">
                      <textarea
                        value={reasonEditing.draft}
                        onChange={(e) =>
                          setReasonEditing({ id: inv.id, draft: e.target.value })
                        }
                        rows={2}
                        maxLength={2000}
                        className="w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[12px]"
                        autoFocus
                      />
                      <div className="flex gap-1.5">
                        <Btn
                          variant="primary"
                          size="sm"
                          onClick={() => void saveReason(inv, reasonEditing.draft)}
                        >
                          Enregistrer
                        </Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => setReasonEditing(null)}
                        >
                          Annuler
                        </Btn>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-[12px] text-text-primary">
                      {inv.reason_for_access || (
                        <span className="italic text-text-tertiary">
                          (raison non renseignée — éditez)
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <details className="mt-2 text-[11px]">
                  <summary className="cursor-pointer text-accent-text hover:underline">
                    Permissions ({Object.values(inv.permissions).filter(Boolean).length} activées)
                  </summary>
                  <table className="mt-2 w-full text-[11px]">
                    <thead>
                      <tr className="text-text-tertiary text-[10px]">
                        <th className="text-left font-normal pb-1">Fonctionnalité</th>
                        <th className="text-center font-normal pb-1 w-12">Lect.</th>
                        <th className="text-center font-normal pb-1 w-14">Écrit.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {FEATURES.map((f) => {
                        const viewChecked = !!inv.permissions[f.viewKey];
                        const editChecked = f.editKey ? !!inv.permissions[f.editKey] : false;
                        return (
                          <tr key={f.viewKey} className="border-t border-rule">
                            <td className="py-1 pr-2">{f.label}</td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={viewChecked}
                                onChange={(e) => {
                                  const patch: Record<string, boolean> = {
                                    [f.viewKey]: e.target.checked,
                                  };
                                  if (!e.target.checked && f.editKey) patch[f.editKey] = false;
                                  void updatePermissions(inv, {
                                    ...inv.permissions,
                                    ...patch,
                                  });
                                }}
                              />
                            </td>
                            <td className="text-center">
                              {f.editKey ? (
                                <input
                                  type="checkbox"
                                  checked={editChecked}
                                  disabled={!viewChecked}
                                  onChange={(e) =>
                                    void updatePermissions(inv, {
                                      ...inv.permissions,
                                      [f.editKey!]: e.target.checked,
                                    })
                                  }
                                />
                              ) : (
                                <span className="text-text-tertiary">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="border-t border-rule">
                        <td className="py-1 pr-2">Ajouter des commentaires</td>
                        <td className="text-center" colSpan={2}>
                          <input
                            type="checkbox"
                            checked={!!inv.permissions.can_comment}
                            onChange={(e) =>
                              void updatePermissions(inv, {
                                ...inv.permissions,
                                can_comment: e.target.checked,
                              })
                            }
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </details>
              </li>
            ))}
          </ul>

          {revoked.length > 0 && (
            <details className="rounded-md border border-rule bg-paper-card p-3 text-[12px]">
              <summary className="cursor-pointer text-text-tertiary">
                Révoquées ({revoked.length})
              </summary>
              <ul className="mt-2 space-y-1.5">
                {revoked.map((inv) => (
                  <li key={inv.id} className="text-text-secondary">
                    <strong>{inv.invited_email}</strong> — révoqué le{' '}
                    {inv.revoked_at && new Date(inv.revoked_at).toLocaleDateString('fr-BE')} :{' '}
                    {inv.revoke_reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
